import { initialize, LDClient, LDContext } from "launchdarkly-js-client-sdk";
import { z } from "zod";
import {
  Context,
  createContext,
  createElement,
  FC,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useSafeContext } from "../hooks/useSafeContext";

type CamelCaseFromKebab<S extends any> = S extends string
  ? S extends `${infer P1}-${infer P2}${infer P3}`
    ? `${Lowercase<P1>}${Uppercase<P2>}${CamelCaseFromKebab<P3>}`
    : Lowercase<S>
  : never;

const toCamelCaseFromKebab = <T extends string>(
  s: T,
): CamelCaseFromKebab<T> => {
  return s.replace(/-./g, (x) => x[1].toUpperCase()) as CamelCaseFromKebab<T>;
};

type FeatureFlagsValues = Readonly<Record<string, any>>;
type FeatureFlagsSchema<FEATURE_FLAG_VALUES extends FeatureFlagsValues> = {
  readonly [KEY in keyof FEATURE_FLAG_VALUES]: z.ZodType<
    FEATURE_FLAG_VALUES[KEY]
  >;
};

type Key<FEATURE_FLAG_VALUES extends FeatureFlagsValues> =
  keyof FEATURE_FLAG_VALUES & string;

export type UseFeatureFlagReturnVal<
  FEATURE_FLAG_VALUES extends FeatureFlagsValues,
  KEY extends keyof FEATURE_FLAG_VALUES,
> = {
  [K in CamelCaseFromKebab<KEY>]: FEATURE_FLAG_VALUES[KEY];
} & {
  getValue: () => FEATURE_FLAG_VALUES[KEY];
};

interface FeatureFlagsClient<FEATURE_FLAG_VALUES extends FeatureFlagsValues> {
  listen: <KEY extends Key<FEATURE_FLAG_VALUES>>(
    flag: KEY,
    callback: (changes: FEATURE_FLAG_VALUES[KEY]) => void,
  ) => () => void;
  evaluateFlag: <KEY extends Key<FEATURE_FLAG_VALUES>>(
    flag: KEY,
  ) => FEATURE_FLAG_VALUES[KEY];
  setUser: (user: string | null) => void;
}

class LDFeatureFlagsClient<
  FEATURE_FLAG_VALUES extends FeatureFlagsValues,
> implements FeatureFlagsClient<FEATURE_FLAG_VALUES> {
  static clientSingleton: LDClient | null;

  private constructor(
    private readonly client: LDClient,
    private readonly schema: FeatureFlagsSchema<FEATURE_FLAG_VALUES>,
    private readonly fallbackValues: FEATURE_FLAG_VALUES,
  ) {}

  static async initialize<FEATURE_FLAG_VALUES extends FeatureFlagsValues>(
    user: string | null,
    launchDarklyKey: string,
    FeatureFlagsSchema: FeatureFlagsSchema<FEATURE_FLAG_VALUES>,
    fallbackValues: FEATURE_FLAG_VALUES,
  ) {
    const client = await this.initializeOrIdentifyClientSingleton(
      user,
      launchDarklyKey,
    );
    return new LDFeatureFlagsClient(client, FeatureFlagsSchema, fallbackValues);
  }

  private static initializeOrIdentifyClientSingleton = async (
    user: string | null,
    launchDarklyKey: string,
  ) => {
    const context: LDContext = user ? { key: user } : { anonymous: true };
    if (this.clientSingleton) {
      await this.clientSingleton.identify(context);
      return this.clientSingleton;
    } else {
      const client = initialize(launchDarklyKey, context, {
        sendEventsOnlyForVariation: true,
        streaming: true,
        logger: {
          debug: () => undefined,
          info: () => undefined,
          warn: () => undefined,
          error: (message: string) =>
            console.error(new Error("LaunchDarkly error"), message),
        },
      });
      await client.waitForInitialization();
      this.clientSingleton = client;
      return client;
    }
  };

  listen<KEY extends Key<FEATURE_FLAG_VALUES>>(
    flag: KEY,
    callback: (changes: FEATURE_FLAG_VALUES[KEY]) => void,
  ) {
    const listenerKey = `change:${flag}`;
    const handleChanges = (update: unknown) => {
      const value = this.schema[flag].parse(update);
      callback(value);
    };
    this.client.on(listenerKey, handleChanges);
    handleChanges(this.evaluateFlag(flag)); // handle initial value
    return () => this.client.off(listenerKey, handleChanges);
  }

  evaluateFlag<KEY extends Key<FEATURE_FLAG_VALUES>>(
    flag: KEY,
  ): FeatureFlagsValues[KEY] {
    const variation = this.client.variation(flag);
    if (variation === null || variation === undefined) {
      console.warn(
        `Failed to load feature flag value from LaunchDarkly for "${flag}", using fallback value ${this.fallbackValues[flag]} instead.`,
      );
      return this.fallbackValues[flag];
    }
    return this.schema[flag].parse(variation);
  }

  setUser(user: string | null) {
    this.client.identify(user === null ? { anonymous: true } : { key: user });
  }
}

class StaticClient<
  FEATURE_FLAG_VALUES extends FeatureFlagsValues,
> implements FeatureFlagsClient<FEATURE_FLAG_VALUES> {
  constructor(private readonly values: FEATURE_FLAG_VALUES) {}

  evaluateFlag<KEY extends keyof FEATURE_FLAG_VALUES>(
    flag: KEY,
  ): FEATURE_FLAG_VALUES[KEY] {
    return this.values[flag];
  }

  listen() {
    return () => {
      /* no op*/
    };
  }

  setUser() {
    /* no op*/
  }
}

export class FeatureFlags<FEATURE_FLAG_VALUES extends FeatureFlagsValues> {
  private readonly context: Context<FeatureFlagsClient<FEATURE_FLAG_VALUES> | null>;
  private readonly errorLogger: (...data: unknown[]) => void;
  private readonly infoLogger: (...data: unknown[]) => void;
  private readonly schema: FeatureFlagsSchema<FEATURE_FLAG_VALUES>;
  private readonly _fallbackValues: FEATURE_FLAG_VALUES;

  constructor(
    errorLogger: (...data: unknown[]) => void,
    infoLogger: (...data: unknown[]) => void,
    schema: FeatureFlagsSchema<FEATURE_FLAG_VALUES>,
    fallbackValues: FEATURE_FLAG_VALUES,
  ) {
    this.context =
      createContext<FeatureFlagsClient<FEATURE_FLAG_VALUES> | null>(null);
    this.errorLogger = errorLogger;
    this.infoLogger = infoLogger;
    this._fallbackValues = fallbackValues;
    this.schema = schema;
  }

  static readonly withSchema = <FEATURE_FLAG_VALUES extends FeatureFlagsValues>(
    schema: FeatureFlagsSchema<FEATURE_FLAG_VALUES>,
  ) => ({
    andFallbackValues: (defaults: FEATURE_FLAG_VALUES) => ({
      andLoggers: (
        errorLogger: (...data: unknown[]) => void,
        infoLogger: (...data: unknown[]) => void,
      ) => new FeatureFlags(errorLogger, infoLogger, schema, defaults),
    }),
  });

  readonly StaticProvider = ({
    children,
    loadingFallback,
  }: PropsWithChildren<{ loadingFallback?: ReactNode }>) => {
    return (
      <this.Provider
        getClient={async () => {
          return new StaticClient(this._fallbackValues);
        }}
        loadingFallback={loadingFallback}
      >
        {children}
      </this.Provider>
    );
  };

  readonly FeatureFlagsProvider: FC<
    PropsWithChildren<{
      user: string | null;
      apiKey: string;
      loadingFallback?: ReactNode;
    }>
  > = ({ user, children, apiKey, loadingFallback }) => {
    // Memoized: an inline arrow would retrigger Provider's effect every
    // render, causing "Maximum update depth exceeded" (e.g. offline/local).
    const getClient = useCallback(
      () =>
        LDFeatureFlagsClient.initialize(
          user,
          apiKey,
          this.schema,
          this._fallbackValues,
        ),
      [user, apiKey],
    );

    return createElement(
      this.Provider,
      { loadingFallback, getClient },
      children,
    );
  };

  readonly useFeatureFlag = <KEY extends Key<FEATURE_FLAG_VALUES>>(
    key: KEY,
  ): UseFeatureFlagReturnVal<FEATURE_FLAG_VALUES, KEY> => {
    const client = useSafeContext(this.context, "FeatureFlag");
    const [flagValue, setFlagValue] = useState<FEATURE_FLAG_VALUES[KEY]>(
      client.evaluateFlag(key),
    );

    useEffect(() => client.listen(key, setFlagValue), [key, client]);

    return {
      [toCamelCaseFromKebab(key)]: flagValue,
      getValue: () => client.evaluateFlag(key),
    } as UseFeatureFlagReturnVal<FEATURE_FLAG_VALUES, KEY>;
  };

  readonly useUpdateFeatureFlagUser = () => {
    const client = useSafeContext(this.context, "FeatureFlag");
    return (user: string | null) => client.setUser(user);
  };

  readonly fallbackValues = () => {
    return Object.fromEntries(
      Object.entries(this._fallbackValues).map(([key, value]) => [
        toCamelCaseFromKebab(key),
        value,
      ]),
    ) as {
      [KEY in keyof FEATURE_FLAG_VALUES as CamelCaseFromKebab<KEY>]: FEATURE_FLAG_VALUES[KEY];
    };
  };

  private Provider: FC<
    PropsWithChildren<{
      loadingFallback?: ReactNode;
      getClient: () => Promise<FeatureFlagsClient<FEATURE_FLAG_VALUES>>;
    }>
  > = ({ children, loadingFallback, getClient }) => {
    const [client, setClient] = useState<
      FeatureFlagsClient<FEATURE_FLAG_VALUES> | "unset" | "loading" | "error"
    >("unset");

    useEffect(() => {
      let mounted = true;
      setClient("loading");
      getClient()
        .then((client) => {
          if (mounted) setClient(client);
        })
        .catch((e) => {
          if (mounted) {
            this.errorLogger(e, "Failed to load feature flags");
            setClient("error");
          }
        });

      return () => {
        mounted = false;
      };
    }, [getClient]);

    if (
      (client === "loading" || client === "unset") &&
      loadingFallback !== undefined
    ) {
      return <>{loadingFallback}</>;
    }

    return (
      <this.context.Provider
        value={
          client === "error" || client === "loading" || client === "unset"
            ? new StaticClient(this._fallbackValues)
            : client
        }
      >
        {children}
      </this.context.Provider>
    );
  };
}
